import {
  faFloppyDisk,
  faPen,
  faPlus,
  faXmark
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useRef, useState } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import LargeCard from '@/components/LargeCard';
import { PERMISSIONS } from '@/constants/auth.constants';

import type { TagManagementProps } from './TagManagement.types';

const isValidTag = (tag: string): boolean => {
  return /^[a-zA-Z0-9_-]+$/.test(tag);
};

const TagManagement = ({ orgInfo, handleSave }: TagManagementProps) => {
  const [editing, setEditing] = useState<boolean>(false);
  const [tags, setTags] = useState<string[]>(orgInfo.tags || []);
  const [tagRemoved, setTagRemoved] = useState<boolean | null>(false);
  const [inputError, setInputError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleEdit = () => {
    setEditing(true);
    setTags(orgInfo.tags || []);
    setInputError('');
  };

  const handleCancel = () => {
    setEditing(false);
    setTags(orgInfo.tags || []);
    setTagRemoved(false);
    setInputError('');
  };

  const handleSaveClick = () => {
    handleSave({
      ...orgInfo,
      tags
    });
    setEditing(false);
    setTagRemoved(false);
  };

  const handleAddTag = () => {
    if (inputRef.current) {
      const { value } = inputRef.current;
      const trimmedTag = value.trim().toLowerCase();

      if (!trimmedTag) {
        return;
      }

      if (tags.includes(trimmedTag)) {
        setInputError('Tag already exists');
        return;
      }

      if (trimmedTag && !isValidTag(trimmedTag)) {
        setInputError(
          'Tags can only contain letters, digits, hyphens, and underscores'
        );
        return;
      }

      setInputError('');
      setTags([...tags, trimmedTag]);
      inputRef.current.value = '';
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
    if (orgInfo.tags?.includes(tagToRemove)) {
      setTagRemoved(true);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddTag();
    }
  };

  return (
    <LargeCard
      title="Tags Management"
      rightContent={
        <CheckPermissions permissions={PERMISSIONS.ADMIN_UPDATE}>
          {editing ? (
            <Stack direction="row">
              <IconButton onClick={handleSaveClick}>
                <FontAwesomeIcon size="xs" icon={faFloppyDisk} />
              </IconButton>
              <IconButton onClick={handleCancel}>
                <FontAwesomeIcon size="xs" icon={faXmark} />
              </IconButton>
            </Stack>
          ) : (
            <IconButton onClick={handleEdit}>
              <FontAwesomeIcon size="xs" icon={faPen} />
            </IconButton>
          )}
        </CheckPermissions>
      }
    >
      <Stack spacing={2}>
        <Typography variant="body1" color="text.secondary">
          Manage tags that can be used to categorize and organize tickets in
          this organization.
        </Typography>

        {editing ? (
          <>
            <InputLabel>New Tag</InputLabel>
            <Stack
              spacing={1}
              direction="row"
              sx={{ mt: '0.5rem !important', alignItems: 'center' }}
            >
              <TextField
                size="small"
                onKeyDown={handleKeyPress}
                sx={{ width: '20rem' }}
                error={!!inputError}
                inputRef={inputRef}
                helperText={
                  inputError ||
                  'Letters, digits, hyphens, and underscores allowed'
                }
                placeholder="Enter tag name"
              />
              <IconButton onClick={handleAddTag} color="primary">
                <FontAwesomeIcon icon={faPlus} />
              </IconButton>
            </Stack>

            {tags.length > 0 ? (
              <Box>
                <InputLabel sx={{ mb: 1 }}>Current Tags</InputLabel>
                <Stack
                  direction="row"
                  sx={{ flexWrap: 'wrap', gap: 0.5 }}
                  spacing={0.5}
                >
                  {tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      variant={
                        orgInfo.tags?.includes(tag) ? 'filled' : 'outlined'
                      }
                      color={
                        orgInfo.tags?.includes(tag) ? 'default' : 'success'
                      }
                    />
                  ))}
                </Stack>
              </Box>
            ) : (
              <Typography
                sx={{ fontStyle: 'italic' }}
                variant="body2"
                color="text.secondary"
              >
                No tags configured. Add a tag above to get started.
              </Typography>
            )}
          </>
        ) : (
          <Box>
            <InputLabel sx={{ mb: 1 }}>Current Tags</InputLabel>
            {orgInfo.tags && orgInfo.tags.length > 0 ? (
              <Stack
                direction="row"
                sx={{ flexWrap: 'wrap', gap: 0.5 }}
                spacing={0.5}
              >
                {orgInfo.tags.map((tag) => (
                  <Chip key={tag} label={tag} variant="filled" />
                ))}
              </Stack>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                No tags configured
              </Typography>
            )}
          </Box>
        )}
        {tagRemoved && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            please note that removing a tag will also remove it from all
            existing tickets.
          </Alert>
        )}
      </Stack>
    </LargeCard>
  );
};

export default TagManagement;
